import { useEffect, useMemo, useRef, useState } from 'react'
import { useFrame } from '@react-three/fiber'
import { useGLTF } from '@react-three/drei'
import * as THREE from 'three'
import { clone as skeletonClone } from 'three/examples/jsm/utils/SkeletonUtils.js'
import { useAppStore } from '../store'
import { getPoseById } from '../poses/library'
import {
  applyRootTransform,
  blendQuats,
  captureBindPose,
  collectBones,
  evaluateCycle,
  getTargetMaps,
} from '../poses/animation'
import { MuscleHighlights } from './MuscleHighlights'
import { LayerOverlays } from './LayerOverlays'

const MODEL_URL = `${import.meta.env.BASE_URL}models/female-rig.glb`
/** CC0 Vitruvian head (ibrews/VitruvianGodot vitruvian_head.glb) — static mesh, parented to the head bone. */
const HEAD_URL = `${import.meta.env.BASE_URL}models/head.glb`

useGLTF.preload(MODEL_URL)
useGLTF.preload(HEAD_URL)

const SKIN = '#e8c4b0'
const HEAD_MATERIALS: Record<string, { color: string; opacity?: number; hide?: boolean }> = {
  VitSkin: { color: SKIN },
  VitMouth: { color: '#a8606a' },
  VitSclera: { color: '#f3efe9' },
  'VitSclera.001': { color: '#f3efe9' },
  VitIris: { color: '#4a3426' },
  'VitIris.001': { color: '#4a3426' },
  VitEyeBack: { color: '#2a1d16' },
  'VitEyeBack.001': { color: '#2a1d16' },
  VitCornea2: { color: '#ffffff', hide: true },
  'VitCornea2.001': { color: '#ffffff', hide: true },
  VitEyeshadow: { color: SKIN, hide: true },
  VitTearline: { color: SKIN, hide: true },
  VitCaruncle: { color: '#d99a94' },
}

export function Avatar() {
  const group = useRef<THREE.Group>(null)
  const { scene } = useGLTF(MODEL_URL)
  const { scene: headScene } = useGLTF(HEAD_URL)
  // SkeletonUtils.clone rebinds SkinnedMesh to the cloned bones (Object3D.clone does not,
  // which left the visible mesh stuck in bind pose).
  const clone = useMemo(() => {
    const c = skeletonClone(scene)
    c.updateMatrixWorld(true)
    let headBone: THREE.Object3D | undefined
    c.traverse((o) => { if ((o as THREE.Bone).isBone && o.name === 'mixamorigHead') headBone = o })
    if (headBone) {
      const head = headScene.clone(true)
      head.name = 'VitruvianHead'
      head.traverse((o) => { o.userData.headAttachment = true })
      head.updateMatrixWorld(true)
      headBone.attach(head) // keeps the shared world-space rest alignment
    }
    return c
  }, [scene, headScene])
  const [bonesMap, setBonesMap] = useState<Map<string, THREE.Bone>>(() => new Map())

  const bindRef = useRef<Map<string, THREE.Quaternion>>(new Map())
  const bonesRef = useRef<Map<string, THREE.Bone>>(new Map())
  const basePos = useRef(new THREE.Vector3(0, 0, 0))
  const baseRot = useRef(new THREE.Euler(0, 0, 0))
  const ready = useRef(false)

  const poseId = useAppStore((s) => s.poseId)
  const speed = useAppStore((s) => s.speed)
  const holdPose = useAppStore((s) => s.holdPose)
  const setElapsedMs = useAppStore((s) => s.setElapsedMs)
  const setPhase = useAppStore((s) => s.setPhase)
  const setModelReady = useAppStore((s) => s.setModelReady)
  const setLoadProgress = useAppStore((s) => s.setLoadProgress)
  const setModelError = useAppStore((s) => s.setModelError)
  const layers = useAppStore((s) => s.layers)
  const cardsMode = useAppStore((s) => s.cardsMode)

  useEffect(() => {
    try {
      setLoadProgress(0.4)
      clone.traverse((obj) => {
        if ((obj as THREE.Mesh).isMesh) {
          obj.castShadow = true
          obj.receiveShadow = true
          const mesh = obj as THREE.Mesh
          if (mesh.material) {
            const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material]
            if (mesh.userData.headAttachment) {
              const remapped = mats.map((mat) => {
                const spec = HEAD_MATERIALS[mat.name] ?? { color: SKIN }
                const nm = new THREE.MeshStandardMaterial({ color: spec.color, roughness: 0.6, metalness: 0.02 })
                nm.name = mat.name
                if (spec.hide) nm.visible = false
                return nm
              })
              mesh.material = Array.isArray(mesh.material) ? remapped : remapped[0]
              if ((mesh as THREE.Mesh).morphTargetInfluences) mesh.morphTargetInfluences?.fill(0)
              return
            }
            mats.forEach((mat) => {
              const m = mat as THREE.MeshStandardMaterial
              if (m.color) {
                if (mesh.name === 'Shirt' || mesh.name === 'Pants') {
                  m.color.set('#4a7c7c')
                  m.roughness = 0.75
                } else {
                  m.color.set('#e8c4b0')
                  m.roughness = 0.65
                  m.metalness = 0.05
                }
              }
            })
          }
        }
      })

      const box = new THREE.Box3().setFromObject(clone)
      const size = box.getSize(new THREE.Vector3())
      const center = box.getCenter(new THREE.Vector3())
      clone.position.x -= center.x
      clone.position.z -= center.z
      clone.position.y -= box.min.y

      const targetH = 1.7
      if (size.y > 0.1) {
        clone.scale.setScalar(targetH / size.y)
        const box2 = new THREE.Box3().setFromObject(clone)
        clone.position.y -= box2.min.y
      }

      bindRef.current = captureBindPose(clone)
      bonesRef.current = collectBones(clone)
      setBonesMap(bonesRef.current)
      basePos.current.copy(clone.position)
      baseRot.current.copy(clone.rotation)
      ready.current = true
      setLoadProgress(1)
      setModelReady(true)
    } catch (e) {
      setModelError(e instanceof Error ? e.message : String(e))
    }
  }, [clone, setLoadProgress, setModelReady, setModelError])

  useEffect(() => {
    if (import.meta.env.DEV || new URLSearchParams(window.location.search).has('debug')) {
      ;(window as unknown as Record<string, unknown>).__avatar = { clone, bones: bonesMap, bind: bindRef.current }
    }
  }, [clone, bonesMap])

  useFrame((_, delta) => {
    if (!ready.current) return
    const pose = getPoseById(poseId)
    if (!pose) return

    const st = useAppStore.getState()
    let elapsed = st.elapsedMs
    if (st.playing && !st.holdPose) {
      elapsed += delta * 1000
      setElapsedMs(elapsed)
    }

    const cycle = evaluateCycle(elapsed, pose, speed, holdPose)
    setPhase(cycle.phase, cycle.poseWeight)

    const { from, to, t } = getTargetMaps(pose, bindRef.current, cycle.phase, cycle.poseWeight)
    blendQuats(from, to, t, bonesRef.current)

    clone.traverse((obj) => {
      const sk = obj as THREE.SkinnedMesh
      if (sk.isSkinnedMesh) sk.skeleton.update()
    })

    applyRootTransform(clone, pose, cycle.poseWeight, basePos.current, baseRot.current)
  })

  const pose = getPoseById(poseId)

  return (
    <group ref={group}>
      <primitive object={clone} />
      {layers.muscles && cardsMode !== 'off' && pose && (
        <MuscleHighlights bones={bonesMap} pose={pose} />
      )}
      <LayerOverlays bones={bonesMap} />
    </group>
  )
}
